<template>
  <div class="chat-app fonts-white"

  >
    <!-- Loading State -->
    <div v-if="getUserVerseUsername === ''" class="loading-screen">
      <v-icon size="64" class="warning-icon">mdi-account-alert</v-icon>
      <h3 class="loading-title">Verse Username Required</h3>
      <p class="loading-text">You need to set your Verse Username to start messaging</p>
      <v-btn
        @click="$router.push('/acc')"
        variant="outlined"
        prepend-icon="mdi-cog"
        class="settings-btn"
      >
        Go to Settings
      </v-btn>
    </div>
    
    <div v-else>
      <div v-if="loading" class="loading-screen">
        <div class="spinner"></div>
        <p class="loading-text">Connecting to chat...</p>
      </div>

      <div v-else-if="error" class="error-screen">
        <v-icon size="64" class="error-icon">mdi-alert-circle</v-icon>
        <h3 class="error-title">Connection Error</h3>
        <p class="error-text">{{ error }}</p>
        <v-btn
          @click="reconnect"
          variant="outlined"
          prepend-icon="mdi-refresh"
          class="reconnect-btn"
        >
          Reconnect
        </v-btn>
      </div>

      <!-- Desktop View -->
      <div v-else-if="!isMobileSm && !isMobileXs && !isMobile" class="chat-interface" :style="{ maxHeight: drawerContentMaxHeight, minHeight: drawerContentMaxHeight }">
        <div class="chat-sidebar" :style="{ maxHeight: drawerContentMaxHeight, minHeight: drawerContentMaxHeight }">
          <!-- Connection Status -->
          <div class="connection-status" :class="{ connected: isConnected }">
            <span class="status-dot"></span>
            <span class="status-text">{{ isConnected ? 'Connected' : 'Disconnected' }}</span>
          </div>

          <!-- Search Section -->
          <div class="search-section">
            <div class="search-wrapper">
              <v-icon class="search-icon" size="18">mdi-magnify</v-icon>
              <input 
                v-model="searchQuery" 
                placeholder="Search chats..." 
                class="search-input"
              />
              <button 
                v-if="searchQuery"
                @click="searchQuery = ''" 
                class="btn-clear"
              >
                <v-icon size="16">mdi-close</v-icon>
              </button>
            </div>
          </div>

          <!-- Notifications -->
          <transition name="slide-down">
            <div v-if="unreadNotifications > 0" class="notifications-section">
              <div class="notification-badge" @click="showNotifications = true">
                <v-icon size="18" color="warning">mdi-bell-ring</v-icon>
                <span class="notification-text">{{ unreadNotifications }} unread notification{{ unreadNotifications > 1 ? 's' : '' }}</span>
                <v-icon size="16" class="notification-arrow">mdi-chevron-right</v-icon>
              </div>
            </div>
          </transition>

          <!-- Chat Tabs -->
          <div class="chat-tabs">
            <div 
              class="chat-tab" 
              :class="{ active: activeTab === 'chats' }"
              @click="activeTab = 'chats'"
            >
              <v-icon left size="18">mdi-message-text</v-icon>
              <span>Chats</span>
              <span class="tab-count">{{ filteredChats.length }}</span>
            </div>
            <div 
              class="chat-tab" 
              :class="{ active: activeTab === 'hidden' }"
              @click="activeTab = 'hidden'"
            >
              <v-icon left size="18">mdi-eye-off</v-icon>
              <span>Hidden</span>
              <span class="tab-count">{{ filteredHiddenChats.length }}</span>
            </div>
          </div>

          <!-- Chat Actions -->
          <div class="chat-actions">
            <button @click="showCreateChat = true" class="btn-action primary">
              <v-icon left size="18">mdi-plus-circle</v-icon>
              New Chat
            </button>
            <button @click="showSettings = true" class="btn-action secondary" title="Settings">
              <v-icon size="20">mdi-cog</v-icon>
            </button>
          </div>

          <!-- Normal Chats List -->
          <div v-if="activeTab === 'chats'" 
            class="chat-list" 
            @scroll="handlechatChatsScroll" 
            @touchstart="handleChatListTouchStart"
            @touchmove="handleChatListTouchMove"
            @touchend="handleChatListTouchEnd"
            ref="chatListContainer"
            tabindex="0"
          >
            <div 
              v-for="chat in filteredChats" 
              :key="chat._id"
              @click="selectChat(chat)"
              class="chat-item"
              :class="{ 
                active: selectedChat?._id === chat._id,
                unread: chat._id !== selectedChat?._id && chat.lastMessage && !chat.lastMessage.seenBy.includes(getCurrentUserId)
              }"
            >
              <div class="chat-avatar">
                {{ getChatInitials(chat) }}
                <span v-if="!chat.isGroup" class="online-indicator" :class="{ online: getUserChatStatusComputed(chat) }"></span>
              </div>
              
              <div class="chat-info">
                <div class="chat-header-row">
                  <div class="chat-name">
                    {{ getChatName(chat) }}
                    <v-icon v-if="chat.isGroup" size="14" class="group-icon">mdi-account-group</v-icon>
                  </div>
                  <div class="chat-time">{{ formatLastMessageTime(chat.lastMessage) }}</div>
                </div>
                
                <div class="chat-preview">
                  <span class="chat-preview-text">{{ chat.lastMessage?.content || 'No messages yet' }}</span>
                  <span v-if="chat._id !== selectedChat?._id && chat.lastMessage && !chat.lastMessage.seenBy.includes(getCurrentUserId)" class="unread-badge">●</span>
                </div>
              </div>
              
              <div class="chat-item-actions">
                <button @click.stop="hideChat(chat._id)" class="btn-icon-small" title="Hide">
                  <v-icon size="16">mdi-eye-off</v-icon>
                </button>
                <button @click.stop="deleteChat(chat._id)" class="btn-icon-small danger" title="Delete">
                  <v-icon size="16">mdi-delete</v-icon>
                </button>
              </div>
            </div>

            <!-- Loading More -->
            <div v-if="loadingChats" class="loading-more">
              <div class="spinner-small"></div>
              <span>Loading more chats...</span>
            </div>

            <!-- Load More Button -->
            <div v-if="chatChatsPagination.hasMore && !loadingChats && chats.length > 0" class="load-more-container">
              <button @click="loadMoreChats" class="btn-load-more">
                <v-icon left size="18">mdi-chevron-down</v-icon>
                Load More Chats ({{ chatChatsPagination.total - chatChatsPagination.currentCount }} remaining)
              </button>
            </div>

            <!-- Empty State -->
            <div v-if="chats.length === 0 && !loadingChats" class="empty-state">
              <v-icon size="64" color="grey">mdi-chat-outline</v-icon>
              <h3>No chats yet</h3>
              <p>Start a conversation by creating a new chat</p>
              <button @click="showCreateChat = true" class="btn-create-first">
                <v-icon left size="18">mdi-plus-circle</v-icon>
                Create Chat
              </button>
            </div>

            <!-- End of List -->
            <div v-if="chats.length > 0 && !chatChatsPagination.hasMore && !loadingChats" class="end-of-list">
              <span>That's all your chats</span>
            </div>
          </div>

          <!-- Hidden Chats List -->
          <div v-if="activeTab === 'hidden'" 
            class="chat-list" 
            @scroll="handleHiddenChatsScroll"
            @touchstart="handleHiddenListTouchStart"
            @touchmove="handleHiddenListTouchMove"
            @touchend="handleHiddenListTouchEnd"
            ref="hiddenListContainer"
            tabindex="0"
          >
            <div 
              v-for="chat in filteredHiddenChats" 
              :key="'hidden-' + chat._id"
              @click="selectChatHidden(chat)"
              class="chat-item hidden-chat"
            >
              <div class="chat-avatar hidden">
                {{ getChatInitials(chat) }}
                <span v-if="!chat.isGroup" class="online-indicator" :class="{ online: getUserChatStatusComputed(chat) }"></span>
              </div>
              
              <div class="chat-info">
                <div class="chat-header-row">
                  <div class="chat-name">
                    {{ getChatName(chat) }}
                    <v-icon v-if="chat.isGroup" size="14" class="group-icon">mdi-account-group</v-icon>
                  </div>
                </div>
                
                <div class="chat-preview">
                  <span class="chat-preview-text">{{ chat.lastMessage?.content || 'No messages yet' }}</span>
                </div>
              </div>
              
              <div class="chat-item-actions">
                <button @click.stop="unhideChat(chat._id)" class="btn-icon-small success" title="Unhide">
                  <v-icon size="16">mdi-eye</v-icon>
                </button>
                <button @click.stop="deleteChat(chat._id)" class="btn-icon-small danger" title="Delete">
                  <v-icon size="16">mdi-delete</v-icon>
                </button>
              </div>
            </div>

            <!-- Loading More -->
            <div v-if="loadingHiddenChats" class="loading-more">
              <div class="spinner-small"></div>
              <span>Loading more...</span>
            </div>

            <!-- Load More Button -->
            <div v-if="hiddenChatsPagination.hasMore && !loadingHiddenChats && hiddenChats.length > 0" class="load-more-container">
              <button @click="loadMoreHiddenChats" class="btn-load-more">
                <v-icon left size="18">mdi-chevron-down</v-icon>
                Load More Hidden ({{ hiddenChatsPagination.total - hiddenChatsPagination.currentCount }} remaining)
              </button>
            </div>

            <!-- Empty State -->
            <div v-if="hiddenChats.length === 0 && !loadingHiddenChats" class="empty-state">
              <v-icon size="64" color="grey">mdi-eye-off-outline</v-icon>
              <h3>No hidden chats</h3>
              <p>Hidden chats will appear here</p>
            </div>

            <!-- End of List -->
            <div v-if="hiddenChats.length > 0 && !hiddenChatsPagination.hasMore && !loadingHiddenChats" class="end-of-list">
              <span>That's all your hidden chats</span>
            </div>
          </div>
        </div>

        <!-- Main Chat Area -->
        <div class="chat-main" :style="{ maxHeight: drawerContentMaxHeight, minHeight: drawerContentMaxHeight }">
          <div v-if="!selectedChat" class="no-chat-selected">
            <v-icon size="80" color="grey">mdi-chat-outline</v-icon>
            <h3>Select a chat to start messaging</h3>
            <p>Choose a conversation from the sidebar or create a new one</p>
          </div>

          <ChatView 
            v-else
            :chat="selectedChat"
            :messages="messages"
            :loadingMessages="loadingMessages"
            :typingUsers="typingUsers"
            :hasMoreMessages="hasMoreMessages"
            @send-message="sendMessage"
            @load-more="loadMoreMessages"
            @react-to-message="reactToMessage"
            @delete-message="deleteMessage"
            @start-typing="startTyping"
            @stop-typing="stopTyping"
            @chat-updated="handleChatUpdated"
            @chat-left="handleChatLeft"
          />
        </div>
      </div>

      <!-- Mobile View -->
      <div v-else class="chat-interface-mobile" :style="{ maxHeight: drawerContentMaxHeight, minHeight: drawerContentMaxHeight }">
        <!-- Sliding Sidebar -->
        <div 
          class="chat-sidebar-mobile" 
          :class="{ 'sidebar-open': sidebarOpen }"
          :style="{ 'padding-bottom': calculatePaddingBottom + 'px' }"
        >
          <!-- Tab Button -->
          <div 
            class="sidebar-tab" 
            @click="toggleSidebar"
          >
            <span class="tab-text">Chats</span>
            <v-icon class="tab-icon">{{ sidebarOpen ? 'mdi-chevron-left' : 'mdi-chevron-right' }}</v-icon>
             <span v-if="unreadChatsCount > 0" class="tab-unread-badge">{{ unreadChatsCount }}</span>
          </div>

          <!-- Sidebar Content -->
          <div class="sidebar-content">
            <!-- Connection Status -->
            <div class="connection-status" :class="{ connected: isConnected }">
              <span class="status-dot"></span>
              <span class="status-text">{{ isConnected ? 'Connected' : 'Disconnected' }}</span>
            </div>

            <!-- Search Section -->
            <div class="search-section">
              <div class="search-wrapper">
                <v-icon class="search-icon" size="18">mdi-magnify</v-icon>
                <input 
                  v-model="searchQuery" 
                  placeholder="Search chats..." 
                  class="search-input"
                />
                <button 
                  v-if="searchQuery"
                  @click="searchQuery = ''" 
                  class="btn-clear"
                >
                  <v-icon size="16">mdi-close</v-icon>
                </button>
              </div>
            </div>

            <!-- Notifications -->
            <transition name="slide-down">
              <div v-if="unreadNotifications > 0" class="notifications-section">
                <div class="notification-badge" @click="showNotifications = true">
                  <v-icon size="18" color="warning">mdi-bell-ring</v-icon>
                  <span class="notification-text">{{ unreadNotifications }} unread</span>
                  <v-icon size="16" class="notification-arrow">mdi-chevron-right</v-icon>
                </div>
              </div>
            </transition>

            <!-- Chat Tabs -->
            <div class="chat-tabs">
              <div 
                class="chat-tab" 
                :class="{ active: activeTab === 'chats' }"
                @click="activeTab = 'chats'"
              >
                <v-icon left size="18">mdi-message-text</v-icon>
                <span>Chats</span>
                <span class="tab-count">{{ filteredChats.length }}</span>
              </div>
              <div 
                class="chat-tab" 
                :class="{ active: activeTab === 'hidden' }"
                @click="activeTab = 'hidden'"
              >
                <v-icon left size="18">mdi-eye-off</v-icon>
                <span>Hidden</span>
                <span class="tab-count">{{ filteredHiddenChats.length }}</span>
              </div>
            </div>

            <!-- Chat Actions -->
            <div class="chat-actions">
              <button @click="showCreateChat = true" class="btn-action primary">
                <v-icon left size="18">mdi-plus-circle</v-icon>
                New Chat
              </button>
              <button @click="showSettings = true" class="btn-action secondary" title="Settings">
                <v-icon size="20">mdi-cog</v-icon>
              </button>
            </div>

            <!-- Normal Chats -->
            <div v-if="activeTab === 'chats'" 
              class="chat-list" 
              ref="chatListMobileContainer"
              tabindex="0"
              @scroll="handlechatChatsScroll" 
              @touchstart="handleChatListMobileTouchStart"
              @touchmove="handleChatListMobileTouchMove"
              @touchend="handleChatListMobileTouchEnd"
            >
              <div 
                v-for="chat in filteredChats" 
                :key="chat._id"
                @click="selectChatMobile(chat)"
                class="chat-item"
                :class="{ 
                  active: selectedChat?._id === chat._id,
                  unread: chat._id !== selectedChat?._id && chat.lastMessage && !chat.lastMessage.seenBy.includes(getCurrentUserId)
                }"
              >
                <div class="chat-avatar">
                  {{ getChatInitials(chat) }}
                  <span v-if="!chat.isGroup" class="online-indicator" :class="{ online: getUserChatStatusComputed(chat) }"></span>
                </div>
                
                <div class="chat-info">
                  <div class="chat-header-row">
                    <div class="chat-name">
                      {{ getChatName(chat) }}
                      <v-icon v-if="chat.isGroup" size="14" class="group-icon">mdi-account-group</v-icon>
                    </div>
                    <div class="chat-time">{{ formatLastMessageTime(chat.lastMessage) }}</div>
                  </div>
                  
                  <div class="chat-preview">
                    <span class="chat-preview-text">{{ chat.lastMessage?.content || 'No messages yet' }}</span>
                    <span v-if="chat._id !== selectedChat?._id && chat.lastMessage && !chat.lastMessage.seenBy.includes(getCurrentUserId)" class="unread-badge">●</span>
                  </div>
                </div>
                
                <div class="chat-item-actions">
                  <button @click.stop="hideChat(chat._id)" class="btn-icon-small" title="Hide">
                    <v-icon size="16">mdi-eye-off</v-icon>
                  </button>
                  <button @click.stop="deleteChat(chat._id)" class="btn-icon-small danger" title="Delete">
                    <v-icon size="16">mdi-delete</v-icon>
                  </button>
                </div>
              </div>

              <div v-if="loadingChats" class="loading-more">
                <div class="spinner-small"></div>
                <span>Loading more...</span>
              </div>

              <!-- Load More Button -->
              <div v-if="chatChatsPagination.hasMore && !loadingChats && chats.length > 0" class="load-more-container">
                <button @click="loadMoreChats" class="btn-load-more">
                  <v-icon left size="18">mdi-chevron-down</v-icon>
                  Load More Chats ({{ chatChatsPagination.total - chatChatsPagination.currentCount }} remaining)
                </button>
              </div>

              <div v-if="chats.length === 0 && !loadingChats" class="empty-state">
                <v-icon size="64" color="grey">mdi-chat-outline</v-icon>
                <h3>No chats yet</h3>
                <p>Start a conversation</p>
                <button @click="showCreateChat = true" class="btn-create-first">
                  <v-icon left size="18">mdi-plus-circle</v-icon>
                  Create Chat
                </button>
              </div>

              <div v-if="chats.length > 0 && !chatChatsPagination.hasMore && !loadingChats" class="end-of-list">
                <span>That's all your chats</span>
              </div>
            </div>

            <!-- Hidden Chats -->
            <div v-if="activeTab === 'hidden'" 
              class="chat-list" 
              ref="hiddenListMobileContainer"
              tabindex="0"
              @scroll="handleHiddenChatsScroll"
              @touchstart="handleHiddenListMobileTouchStart"
              @touchmove="handleHiddenListMobileTouchMove"
              @touchend="handleHiddenListMobileTouchEnd"
            >
              <div 
                v-for="chat in filteredHiddenChats" 
                :key="'hidden-' + chat._id"
                @click="selectChatHidden(chat)"
                class="chat-item hidden-chat"
              >
                <div class="chat-avatar hidden">
                  {{ getChatInitials(chat) }}
                  <span v-if="!chat.isGroup" class="online-indicator" :class="{ online: getUserChatStatusComputed(chat) }"></span>
                </div>
                
                <div class="chat-info">
                  <div class="chat-header-row">
                    <div class="chat-name">
                      {{ getChatName(chat) }}
                      <v-icon v-if="chat.isGroup" size="14" class="group-icon">mdi-account-group</v-icon>
                    </div>
                  </div>
                  
                  <div class="chat-preview">
                    <span class="chat-preview-text">{{ chat.lastMessage?.content || 'No messages yet' }}</span>
                  </div>
                </div>
                
                <div class="chat-item-actions">
                  <button @click.stop="unhideChat(chat._id)" class="btn-icon-small success" title="Unhide">
                    <v-icon size="16">mdi-eye</v-icon>
                  </button>
                  <button @click.stop="deleteChat(chat._id)" class="btn-icon-small danger" title="Delete">
                    <v-icon size="16">mdi-delete</v-icon>
                  </button>
                </div>
              </div>

              <div v-if="loadingHiddenChats" class="loading-more">
                <div class="spinner-small"></div>
                <span>Loading more...</span>
              </div>

                <!-- Load More Button -->
              <div v-if="hiddenChatsPagination.hasMore && !loadingHiddenChats && hiddenChats.length > 0" class="load-more-container">
                <button @click="loadMoreHiddenChats" class="btn-load-more">
                  <v-icon left size="18">mdi-chevron-down</v-icon>
                  Load More Hidden ({{ hiddenChatsPagination.total - hiddenChatsPagination.currentCount }} remaining)
                </button>
              </div>

              <div v-if="hiddenChats.length === 0 && !loadingHiddenChats" class="empty-state">
                <v-icon size="64" color="grey">mdi-eye-off-outline</v-icon>
                <h3>No hidden chats</h3>
                <p>Hidden chats will appear here</p>
              </div>

              <div v-if="hiddenChats.length > 0 && !hiddenChatsPagination.hasMore && !loadingHiddenChats" class="end-of-list">
                <span>That's all your hidden chats</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Main Area -->
        <div class="chat-main-mobile" :class="{ 'sidebar-open': sidebarOpen }">
          <div v-if="!selectedChat" class="no-chat-selected">
            <v-icon size="80" color="grey">mdi-chat-outline</v-icon>
            <h3>Select a chat</h3>
            <p>Tap the Chats tab to view your conversations</p>
          </div>

          <ChatView 
            v-else
            :chat="selectedChat"
            :messages="messages"
            :loadingMessages="loadingMessages"
            :typingUsers="typingUsers"
            :hasMoreMessages="hasMoreMessages"
            @send-message="sendMessage"
            @load-more="loadMoreMessages"
            @react-to-message="reactToMessage"
            @delete-message="deleteMessage"
            @start-typing="startTyping"
            @stop-typing="stopTyping"
            @chat-updated="handleChatUpdated"
            @chat-left="handleChatLeft"
          />
        </div>

        <!-- Overlay -->
        <div 
          v-if="sidebarOpen" 
          class="sidebar-overlay"
          @click="closeSidebar"
        ></div>
      </div>
    </div>

    <!-- Modals -->
    <CreateChatModal 
      v-if="showCreateChat"
      @close="showCreateChat = false"
      @chat-created="onChatCreated"
    />

    <NotificationsModal 
      v-if="showNotifications"
      @close="showNotifications = false"
    />

    <SettingsModal 
      v-if="showSettings"
      @close="showSettings = false"
    />
  </div>
</template>

<script>
import chatService from '@/services/chat/ChatService'
import ChatView from '@/views/chat/ChatView.vue'
import CreateChatModal from '@/components/chat/CreateChatModal.vue'
import NotificationsModal from '@/components/chat/NotificationsModal.vue'
import SettingsModal from '@/components/chat/SettingsModal.vue'
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useMobile } from '@/composables/useMobile.js'
import { useMobileXs } from '@/composables/useMobileXs.js'
import { useMobileSm } from '@/composables/useMobileSm.js'
import { useMobileMd } from '@/composables/useMobileMd.js'
import { useMobilePlayer3 } from '@/composables/useMobilePlayer3.js'
import { useMobilePlayer2 } from '@/composables/useMobilePlayer2.js'

export default {

  inject: ['playerState'],

  setup() {
    const isMobile = useMobile()
    const isMobileXs = useMobileXs()
    const isMobileSm = useMobileSm()
    const isMobileMd = useMobileMd()
    const isMobilePlayer3 = useMobilePlayer3()
    const isMobilePlayer2 = useMobilePlayer2();

    const notify = (message) => {
      toast(message, {
        theme: "dark",
        type: "success",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 1500,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyError = (message) => {
      toast(message, {
        theme: "dark",
        type: "error",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyWarning = (message) => {
      toast(message, {
        theme: "dark",
        type: "warning",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    return { isMobile, isMobileXs, isMobileSm, isMobileMd, isMobilePlayer3, isMobilePlayer2, notify, notifyError, notifyWarning }
  },
  name: 'ChatApp',
  components: {
    ChatView,
    CreateChatModal,
    NotificationsModal,
    SettingsModal
  },
  props: {
    token: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      isConnected: false,

      chats: [],
      hiddenChats: [],
      selectedChat: null,
      searchQuery: '',
      activeTab: 'chats',
      
      messages: [],
      loadingMessages: false,
      currentPage: 1,
      hasMoreMessages: false,
      
      showCreateChat: false,
      showNotifications: false,
      showSettings: false,
      
      typingUsers: new Map(),
      unreadNotifications: 0,    
      hiddenChatsPagination: {
        page: 1,
        limit: 10,
        total: 1,
        totalPages: 1,
        hasMore: false,
        currentCount: 0,
      },
      chatChatsPagination: {
        page: 1,
        limit: 10,
        total: 1,
        totalPages: 1,
        hasMore: false,
        currentCount: 0,
      },
      loadingHiddenChats: false,
      loadingChats: false,
      hiddenChatsScrollDebounce: null,
      chatChatsScrollDebounce: null,

      sidebarOpen: false,

      touchStartY: 0,
      touchStartX: 0,
      isChatListTouched: false,
      isHiddenListTouched: false,
      isChatListMobileTouched: false,
      isHiddenListMobileTouched: false,
    }
  },
  computed: {
    unreadChatsCount() {
      return this.filteredChats.filter(chat => 
        chat._id !== this.selectedChat?._id && 
        chat.lastMessage && 
        !chat.lastMessage.seenBy.includes(this.getCurrentUserId)
      ).length
    },
    calculatePaddingBottom() {
      // Wysokość playera - musi być zsynchronizowana z v-bottom-navigation
      let playerHeight = 0;
      const playerActive = this.playerState ? this.playerState() : true;
      if (playerActive) {
        playerHeight = this.isMobilePlayer3 ? 130 : 90;
      } else {
        playerHeight = 18;
      }
      
      return playerHeight;
    },
    drawerContentMaxHeight() {
    
    // Wysokość playera - musi być zsynchronizowana z v-bottom-navigation
    let playerHeight = 0;
    const playerActive = this.playerState ? this.playerState() : true;
    if (playerActive) {
      playerHeight = this.isMobilePlayer3 ? 202 : 168;
    } else {
      playerHeight = 102;
    }
    
    const totalOffset = playerHeight;
    return `calc(100dvh - ${totalOffset}px)`;
  },
    getUserVerseUsername() {
      return this.$store.getters.getUserData.verse_username || ''
    },
    getCurrentUserId() {
      return this.$store.getters.getUserData.id || 'current-user-id'
    },
    filteredChats() {
      if (!this.searchQuery) return this.chats
      
      const query = this.searchQuery.toLowerCase()
      return this.chats.filter(chat => {
        const name = this.getChatName(chat).toLowerCase()
        return name.includes(query)
      })
    },
    filteredHiddenChats() {
      if (!this.searchQuery) return this.hiddenChats
      
      const query = this.searchQuery.toLowerCase()
      return this.hiddenChats.filter(chat => {
        const name = this.getChatName(chat).toLowerCase()
        return name.includes(query)
      })
    },
    getUserChatStatusComputed() {
      return (chat) => {
        if (chat.isGroup) return false
        const otherUser = chat.users.find(user => user[0] !== this.getCurrentUserId)
        if (!otherUser) return false
        
        return chatService.userStatusesCache.get(otherUser[0]) || false
      }
    }
  },
  async mounted() {
    await this.initializeChat()
  },
  beforeUnmount() {
    this.cleanup()
  },
  methods: {
    formatLastMessageTime(lastMessage) {
      if (!lastMessage?.createdAt) return ''
      
      const date = new Date(lastMessage.createdAt)
      const now = new Date()
      const diff = now - date
      
      // Less than 1 minute
      if (diff < 60000) return 'Now'
      
      // Less than 1 hour
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000)
        return `${minutes}m`
      }
      
      // Today
      if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
      
      // Yesterday
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday'
      }
      
      // This week
      if (diff < 604800000) {
        return date.toLocaleDateString([], { weekday: 'short' })
      }
      
      // Older
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    },

    getChatInitials(chat) {
      const name = this.getChatName(chat)
      return name.substring(0, 2).toUpperCase()
    },

    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen
    },

    closeSidebar() {
      this.sidebarOpen = false
    },

    selectChatMobile(chat) {
      this.selectChat(chat)
      this.closeSidebar()
    },

    handleChatListTouchStart(event) {
      this.isChatListTouched = true
      this.touchStartY = event.touches[0].clientY
      this.touchStartX = event.touches[0].clientX
      
      const container = this.$refs.chatListContainer
      if (container) {
        container.focus({ preventScroll: true })
      }
    },

    handleChatListTouchMove(event) {
      if (!this.isChatListTouched) return
      
      const container = this.$refs.chatListContainer
      if (!container) return

      const touchY = event.touches[0].clientY
      const deltaY = touchY - this.touchStartY

      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      const isAtTop = scrollTop <= 0
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

      const scrollingUp = deltaY > 0
      const scrollingDown = deltaY < 0

      if ((scrollingUp && !isAtTop) || (scrollingDown && !isAtBottom)) {
        event.stopPropagation()
      }
    },

    handleChatListTouchEnd(event) {
      this.isChatListTouched = false
      this.touchStartY = 0
      this.touchStartX = 0
    },

    handleHiddenListTouchStart(event) {
      this.isHiddenListTouched = true
      this.touchStartY = event.touches[0].clientY
      this.touchStartX = event.touches[0].clientX
      
      const container = this.$refs.hiddenListContainer
      if (container) {
        container.focus({ preventScroll: true })
      }
    },

    handleHiddenListTouchMove(event) {
      if (!this.isHiddenListTouched) return
      
      const container = this.$refs.hiddenListContainer
      if (!container) return

      const touchY = event.touches[0].clientY
      const deltaY = touchY - this.touchStartY

      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      const isAtTop = scrollTop <= 0
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

      const scrollingUp = deltaY > 0
      const scrollingDown = deltaY < 0

      if ((scrollingUp && !isAtTop) || (scrollingDown && !isAtBottom)) {
        event.stopPropagation()
      }
    },

    handleHiddenListTouchEnd(event) {
      this.isHiddenListTouched = false
      this.touchStartY = 0
      this.touchStartX = 0
    },

    handleChatListMobileTouchStart(event) {
      this.isChatListMobileTouched = true
      this.touchStartY = event.touches[0].clientY
      this.touchStartX = event.touches[0].clientX
      
      const container = this.$refs.chatListMobileContainer
      if (container) {
        container.focus({ preventScroll: true })
      }
    },

    handleChatListMobileTouchMove(event) {
      if (!this.isChatListMobileTouched) return
      
      const container = this.$refs.chatListMobileContainer
      if (!container) return

      const touchY = event.touches[0].clientY
      const deltaY = touchY - this.touchStartY

      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      const isAtTop = scrollTop <= 0
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

      const scrollingUp = deltaY > 0
      const scrollingDown = deltaY < 0

      if ((scrollingUp && !isAtTop) || (scrollingDown && !isAtBottom)) {
        event.stopPropagation()
      }
    },

    handleChatListMobileTouchEnd(event) {
      this.isChatListMobileTouched = false
      this.touchStartY = 0
      this.touchStartX = 0
    },

    handleHiddenListMobileTouchStart(event) {
      this.isHiddenListMobileTouched = true
      this.touchStartY = event.touches[0].clientY
      this.touchStartX = event.touches[0].clientX
      
      const container = this.$refs.hiddenListMobileContainer
      if (container) {
        container.focus({ preventScroll: true })
      }
    },

    handleHiddenListMobileTouchMove(event) {
      if (!this.isHiddenListMobileTouched) return
      
      const container = this.$refs.hiddenListMobileContainer
      if (!container) return

      const touchY = event.touches[0].clientY
      const deltaY = touchY - this.touchStartY

      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      const isAtTop = scrollTop <= 0
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

      const scrollingUp = deltaY > 0
      const scrollingDown = deltaY < 0

      if ((scrollingUp && !isAtTop) || (scrollingDown && !isAtBottom)) {
        event.stopPropagation()
      }
    },

    handleHiddenListMobileTouchEnd(event) {
      this.isHiddenListMobileTouched = false
      this.touchStartY = 0
      this.touchStartX = 0
    },

    handleHiddenChatsScroll(event) {
      const container = event.target
      const scrollPosition = container.scrollTop + container.clientHeight
      const scrollHeight = container.scrollHeight
      
      const threshold = 100
      
      if (scrollHeight - scrollPosition < threshold && !this.loadingHiddenChats) {
        clearTimeout(this.hiddenChatsScrollDebounce)
        this.hiddenChatsScrollDebounce = setTimeout(() => {
          this.loadMoreHiddenChats()
        }, 200)
      }
    },

    handlechatChatsScroll(event) {
      const container = event.target
      const scrollPosition = container.scrollTop + container.clientHeight
      const scrollHeight = container.scrollHeight
      
      const threshold = 100
      
      if (scrollHeight - scrollPosition < threshold && !this.loadingChats) {
        clearTimeout(this.chatChatsScrollDebounce)
        this.chatChatsScrollDebounce = setTimeout(() => {
          this.loadMoreChats()
        }, 200)
      }
    },

    getChatById(id) {
      return this.chats.find(chat => chat._id === id)
    },

    async getUserChatStatusAsync(chat) {
      if (chat.isGroup) return false
      const otherUser = chat.users.find(user => user[0] !== this.getCurrentUserId)
      if (!otherUser) return false
      const res = await chatService.getUserStatusCache(otherUser[0])
      return res
    },

    async initializeChat() {
      try {
        if (this.getUserVerseUsername !== ''){
            this.loading = true
            chatService.clearUserStatusCache()
            chatService.setToken(this.token)
            
            this.setupEventListeners()
            chatService.connect()
            
            await this.loadChats()
            await this.loadHiddenChats()
            await this.loadUnreadNotifications()

            for (const chat of this.chats) {
              await this.getUserChatStatusAsync(chat)
            }

            for(const chath of this.hiddenChats) {
              await this.getUserChatStatusAsync(chath)
            }
            
            this.loading = false
        }
      } catch (error) {
        this.error = error.message
        this.loading = false
      }
    },

    removeEventListeners() {
      chatService.off('connected', this.onConnected)
      chatService.off('disconnected', this.onDisconnected)
      chatService.off('connectionError', this.onConnectionError)
      chatService.off('newMessage', this.handleNewMessage)
      chatService.off('messagesSeen', this.handleMessagesSeen)
      chatService.off('messageReactionUpdated', this.handleReactionUpdate)
      chatService.off('messageDeleted', this.handleMessageDeleted)
      chatService.off('chatCreated', this.handleChatCreated)
      chatService.off('chatUpdated', this.handleChatUpdated)
      chatService.off('userAddedToChat', this.onUserAddedToChat)
      chatService.off('userRemovedFromChat', this.onUserRemovedFromChat)
      chatService.off('removedFromChat', this.onRemovedFromChat)
      chatService.off('chatDeletedForUser', this.onChatDeletedForUser)
      chatService.off('chatPermanentlyDeleted', this.onChatPermanentlyDeleted)
      chatService.off('userTyping', this.onUserTyping)
      chatService.off('userStoppedTyping', this.onUserStoppedTyping)
      chatService.off('unreadNotificationsCount', this.onUnreadNotificationsCount)
      chatService.off('rateLimitExceeded', this.onRateLimitExceeded)
      chatService.off('serverShutdown', this.onServerShutdown)
      chatService.off('duplicateConnection', this.onDuplicateConnection)
      chatService.off('userLeftChat', this.onUserLeftChat)
      chatService.off('chatLeft', this.onChatLeft)
      chatService.off('chatHidden', this.onChatHidden)
      chatService.off('chatUnhidden', this.onChatUnhidden)
      chatService.off('chatRestored', this.onChatRestored)
      chatService.off('userOnline', this.onUserOnline)
      chatService.off('userOffline', this.onUserOffline)
      chatService.off('messageTooLong', this.onMessageTooLong)
      chatService.off('autoJoinedRoom', this.onAutoJoinedRoom)
      chatService.off('gotoChat', this.onGotoChat)
      chatService.off('userStatusChanged', this.onUserStatusChanged)
      chatService.off('messageBlocked', this.onMessageBlocked)
      chatService.off('messageBlockedByPrivacy', this.onMessageBlockedByPrivacy)
      chatService.off('gotoEmptyChat', this.onGotoEmptyChat)
    },

    setupEventListeners() {
      this.removeEventListeners()
      chatService.on('connected', this.onConnected)
      chatService.on('disconnected', this.onDisconnected)
      chatService.on('connectionError', this.onConnectionError)
      chatService.on('userStatusChanged', this.onUserStatusChanged)
      chatService.on('newMessage', this.handleNewMessage)
      chatService.on('messagesSeen', this.handleMessagesSeen)
      chatService.on('messageReactionUpdated', this.handleReactionUpdate)
      chatService.on('messageDeleted', this.handleMessageDeleted)
      chatService.on('chatCreated', this.handleChatCreated)
      chatService.on('chatUpdated', this.handleChatUpdated)
      chatService.on('userAddedToChat', this.onUserAddedToChat)
      chatService.on('userRemovedFromChat', this.onUserRemovedFromChat)
      chatService.on('removedFromChat', this.onRemovedFromChat)
      chatService.on('chatDeletedForUser', this.onChatDeletedForUser)
      chatService.on('chatPermanentlyDeleted', this.onChatPermanentlyDeleted)
      chatService.on('userLeftChat', this.onUserLeftChat)
      chatService.on('chatLeft', this.onChatLeft)
      chatService.on('chatHidden', this.onChatHidden)
      chatService.on('chatUnhidden', this.onChatUnhidden)
      chatService.on('chatRestored', this.onChatRestored)
      chatService.on('messageTooLong', this.onMessageTooLong)
      chatService.on('autoJoinedRoom', this.onAutoJoinedRoom)
      chatService.on('messageBlocked', this.onMessageBlocked)
      chatService.on('messageBlockedByPrivacy', this.onMessageBlockedByPrivacy)
      chatService.on('userOnline', this.onUserOnline)
      chatService.on('userOffline', this.onUserOffline)
      chatService.on('userTyping', this.onUserTyping)
      chatService.on('userStoppedTyping', this.onUserStoppedTyping)
      chatService.on('unreadNotificationsCount', this.onUnreadNotificationsCount)
      chatService.on('rateLimitExceeded', this.onRateLimitExceeded)
      chatService.on('serverShutdown', this.onServerShutdown)
      chatService.on('duplicateConnection', this.onDuplicateConnection)     
      chatService.on('gotoChat', this.onGotoChat) 
      chatService.on('gotoEmptyChat', this.onGotoEmptyChat)
    },

    onConnected() {
      this.isConnected = true
      this.error = null
    },

    onDisconnected(reason) {
      this.isConnected = false
    },

    onConnectionError(error) {
      this.showToast('Connection error', 'error')
    },

    onGotoChat(data) {
      const chat = this.getChatById(data.chatId)
      const isHidden = this.hiddenChats.some(hiddenChat => hiddenChat._id === data.chatId)
      if (isHidden) {
        this.showToast('This chat is hidden. Unhide it to view messages.', 'warning')
        return
      }
      if (chat) {
        this.activeTab = 'chats'
        this.selectChat(chat)
      }
    },

    selectChatHidden(chat) {
        this.activeTab = 'hidden'
        this.showToast('This chat is hidden. Unhide it to view messages.', 'warning')
        return
    },

    onUserStatusChanged(data) {
      if (this.selectedChat && this.selectedChat.users.some(u => u[0] === data.userId)) {
        this.selectedChat = { ...this.selectedChat }
      }
    },

    onUserAddedToChat(data) {
      this.showToast(`User added to chat`, 'info')
    },

    onChatLeft(data) {
      this.showToast(`You left a chat`, 'warning')
      this.loadChatsUpdate(this.chatChatsPagination.page)
      this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
      if (this.selectedChat?._id === data.chatId) {
        this.selectedChat = null
        this.messages = []
      }
    },

    onChatHidden(data) {
      this.showToast(`Chat hidden`, 'info')
      this.loadChatsUpdate(this.chatChatsPagination.page)
      this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
    },

    onGotoEmptyChat() {
      this.selectedChat = null
      this.messages = []
    },

    onUserOnline(data) {
      chatService.setUserStatusCache(data.userId, true)
    },

    onUserOffline(data) {
      chatService.setUserStatusCache(data.userId, false)
    },

    onAutoJoinedRoom(data) {
      this.loadChatsUpdate(this.chatChatsPagination.page)
      this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
    },

    onChatRestored(data) {
      this.showToast(`Chat restored`, 'info')
      this.loadChatsUpdate(this.chatChatsPagination.page)
      this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
    },

    onMessageTooLong(data) {
      this.showToast('Your message is too long', 'warning')
    },

    onUserLeftChat(data) {
      this.showToast(`User left the chat`, 'info')
    },

    onUserRemovedFromChat(data) {
      this.showToast(`User removed from chat`, 'info')
    },

    onChatUnhidden(data) {
      this.showToast(`Chat unhidden`, 'info')
      this.loadChatsUpdate(this.chatChatsPagination.page)
      this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
    },

    onRemovedFromChat(data) {
      this.showToast('You were removed from a chat', 'warning')
      this.loadChatsUpdate(this.chatChatsPagination.page)
      this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
      if (this.selectedChat?._id === data.chatId) {
        this.selectedChat = null
        this.messages = []
      }
    },

    onChatDeletedForUser(data) {
      this.showToast('Chat deleted', 'info')
      this.loadChatsUpdate(this.chatChatsPagination.page)
      this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
      if (this.selectedChat?._id === data.chatId) {
        this.selectedChat = null
        this.messages = []
      }
    },

    onChatPermanentlyDeleted(data) {
      this.showToast('Chat permanently deleted', 'warning')
      this.loadChatsUpdate(this.chatChatsPagination.page)
      this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
      if (this.selectedChat?._id === data.chatId) {
        this.selectedChat = null
        this.messages = []
      }
    },

    onUserTyping(data) {
      if (data.chatId === this.selectedChat?._id) {
        this.typingUsers.set(data.senderId, data.username)
        this.typingUsers = new Map(this.typingUsers)
      }
    },

    onUserStoppedTyping(data) {
      this.typingUsers.delete(data.senderId)
      this.typingUsers = new Map(this.typingUsers)
    },

    onUnreadNotificationsCount(data) {
      this.unreadNotifications = data.count
    },

    onMessageBlocked(data) {
      this.showToast('Your message was blocked by the user', 'warning')
    },

    onMessageBlockedByPrivacy(data) {
      this.showToast('Your message was blocked due to recipient\'s privacy settings', 'warning')
    },

    onRateLimitExceeded(data) {
      this.showToast(data.error, 'error')
    },

    onServerShutdown(data) {
      this.showToast('Server is shutting down for maintenance', 'warning')
      setTimeout(() => {
        this.$router.push('/')
      }, 4000)
    },

    onDuplicateConnection(data) {
      this.showToast('You\'ve been connected from another device', 'warning')
    },

    async loadChats(page = 1, limit = 10) {
      try {
        const response = await chatService.getChats(page, limit)
        this.chats = response.chats
        this.chatChatsPagination = response.pagination
      } catch (error) {
        this.showToast('Failed to load chats', 'error')
      }
    },

    async loadChatsUpdate(pages){
        const response2 = await chatService.getChats(1, 10)
        if (pages === 1) {
            this.chats = response2.chats
            this.chatChatsPagination = response2.pagination
            return
        }
        var tempChats = response2.chats
        for (let page = 2; page <= pages; page++) {
            const response = await chatService.getChats(page, 10)
            tempChats = [...tempChats, ...response.chats]
            if (page === pages) {
                this.chats = tempChats;
                this.chatChatsPagination = response.pagination
            }
        }
    },

    async loadMoreChats() {
      if (!this.chatChatsPagination.hasMore || this.loadingChats) {
        return
      }

      try {
        this.loadingChats = true
        
        const nextPage = this.chatChatsPagination.page + 1
        const response = await chatService.getChats(nextPage, 10)
        
        this.chats = [...this.chats, ...response.chats]
        this.chatChatsPagination = response.pagination
                
      } catch (error) {
        this.showToast('Failed to load more chats', 'error')
      } finally {
        this.loadingChats = false
      }
    },

    async loadHiddenChats(page = 1, limit = 10) {
        const response = await chatService.getHiddenChats(page, limit)
        this.hiddenChats = response.chats
        this.hiddenChatsPagination = response.pagination
    },

    async loadHiddenChatsUpdate(pages) {
        const response2 = await chatService.getHiddenChats(1, 10)
        if (pages === 1) {
            this.hiddenChats = response2.chats
            this.hiddenChatsPagination = response2.pagination
            return
        }
        var tempChats = response2.chats
        for (let page = 2; page <= pages; page++) {
            const response = await chatService.getHiddenChats(page, 10)
            tempChats = [...tempChats, ...response.chats]
            if (page === pages) {
                this.hiddenChats = tempChats;
                this.hiddenChatsPagination = response.pagination
            }
        }
    },

    async loadMoreHiddenChats() {
      if (!this.hiddenChatsPagination.hasMore || this.loadingHiddenChats) {
        return
      }

      try {
        this.loadingHiddenChats = true
        
        const nextPage = this.hiddenChatsPagination.page + 1
        const response = await chatService.getHiddenChats(nextPage, 10)
        
        this.hiddenChats = [...this.hiddenChats, ...response.chats]
        this.hiddenChatsPagination = response.pagination
                
      } catch (error) {
        this.showToast('Failed to load more chats', 'error')
      } finally {
        this.loadingHiddenChats = false
      }
    },

    async loadUnreadNotifications() {
        const result = await chatService.getUnreadNotificationsCount()
        this.unreadNotifications = result.unreadCount
    },

    async selectChat(chat) {
      if (this.selectedChat?._id === chat._id) return
      
      this.selectedChat = chat
      this.messages = []
      this.currentPage = 1
      this.typingUsers.clear()
      
      chatService.joinRoom(chat._id)
      await this.loadMessages()
      chatService.markAsSeen(chat._id)
    },

    async loadMessages() {
      if (this.loadingMessages) return
      
      try {
        this.loadingMessages = true
        
        const result = await chatService.getMessages(
          this.selectedChat._id, 
          this.currentPage, 
          50
        )
        
        if (this.currentPage === 1) {
          this.messages = result.messages
        } else {
          this.messages = [...result.messages, ...this.messages]
        }
        
        this.hasMoreMessages = result.pagination.hasMore
        
      } catch (error) {
        this.showToast('Failed to load messages', 'error')
      } finally {
        this.loadingMessages = false
      }
    },

    async loadMoreMessages() {
      if (!this.hasMoreMessages || this.loadingMessages) return
      
      this.currentPage++
      await this.loadMessages()
    },

    sendMessage(content) {
      if (!this.selectedChat || !content.msg?.trim()) return
      
      chatService.sendMessage(this.selectedChat._id, content)
    },

    startTyping() {
      if (!this.selectedChat) return
      chatService.startTyping(this.selectedChat._id)
    },

    stopTyping() {
      if (!this.selectedChat) return
      chatService.stopTyping(this.selectedChat._id)
    },

    reactToMessage(messageId, reaction) {
      chatService.reactToMessage(messageId, reaction)
    },

    async deleteMessage(messageId) {
      try {
        await chatService.deleteMessage(messageId)
        this.showToast('Message deleted', 'success')
      } catch (error) {
        this.showToast('Failed to delete message', 'error')
      }
    },

    async hideChat(chatId) {
      try {
        await chatService.hideChat(chatId)
        await this.loadChatsUpdate(this.chatChatsPagination.page)
        await this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
        
        if (this.selectedChat?._id === chatId) {
          this.selectedChat = null
          this.messages = []
        }
      } catch (error) {
        this.showToast('Failed to hide chat', 'error')
      }
    },

    async unhideChat(chatId) {
      try {
        await chatService.unhideChat(chatId)
        await this.loadChatsUpdate(this.chatChatsPagination.page)
        await this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
      } catch (error) {
        this.showToast('Failed to unhide chat', 'error')
      }
    },

    async deleteChat(chatId) {
      try {
        await chatService.deleteChat(chatId)
        await this.loadChatsUpdate(this.chatChatsPagination.page)
        await this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
        
        if (this.selectedChat?._id === chatId) {
          this.selectedChat = null
          this.messages = []
        }
      } catch (error) {
        this.showToast('Failed to delete chat', 'error')
      }
    },

    async onChatCreated(chat) {
      this.showCreateChat = false
      await this.loadChatsUpdate(this.chatChatsPagination.page)
      this.selectChat(chat)
      this.showToast('Chat created successfully', 'success')
    },

    handleNewMessage(data) {
      if (data.chatId === this.selectedChat?._id) {
        if (data.recipientStatuses === undefined) {
          this.messages.push(data)
          setTimeout(() => {
            chatService.markAsSeen(data.chatId)
          }, 1000)
        }
      }

      if (data.recipientStatuses === undefined) {
        this.loadChatsUpdate(this.chatChatsPagination.page)
        this.loadHiddenChatsUpdate(this.hiddenChatsPagination.page)
      }    
    },

    handleMessagesSeen(data) {
      if (data.chatId === this.selectedChat?._id) {
        data.messageIds.forEach(messageId => {
          const message = this.messages.find(m => m._id === messageId)
          if (message && !message.seenBy.includes(data.userId)) {
            message.seenBy.push(data.userId)
          }
        })

        const chatIndex = this.chats.findIndex(c => c._id === data.chatId)
        if (chatIndex > -1 && this.chats[chatIndex].lastMessage) {
          if (!this.chats[chatIndex].lastMessage.seenBy.includes(data.userId)) {
            this.chats[chatIndex].lastMessage.seenBy.push(data.userId)
          }
        }
      }
    },

    handleReactionUpdate(data) {
      if (this.selectedChat) {
        const message = this.messages.find(m => m._id === data.messageId)
        if (message) {
          message.reactions = data.reactions
        }
      }
    },

    handleMessageDeleted(data) {
      if (this.selectedChat) {
        const index = this.messages.findIndex(m => m._id === data.messageId)
        if (index > -1) {
          this.messages.splice(index, 1)
        }
      }
    },

    handleChatCreated(data) {
      this.loadChatsUpdate(this.chatChatsPagination.page)
    },

    handleChatUpdated(data) {
      this.loadChatsUpdate(this.chatChatsPagination.page)
      
      if (this.selectedChat?._id === data.chat._id) {
        this.selectedChat = data.chat
      }
    },

    handleChatLeft() {
      this.loadChatsUpdate(this.chatChatsPagination.page)
    },

    getChatName(chat) {
      if (chat.isGroup) {
        return chat.groupName || `Group (${chat.users.length} members)`
      }
      const otherUser = chat.users.find(user => user[0] !== this.getCurrentUserId)
      return otherUser ? otherUser[1] : 'Unknown User'
    },

    showToast(message, type = 'info') {
      if (type === 'error') {
        this.notifyError(message)
      } else if (type === 'warning'){
        this.notifyWarning(message)
      } else {
        this.notify(message)
      }
    },

    async reconnect() {
      this.error = null
      this.loading = true
      
      chatService.disconnect()
      await new Promise(resolve => setTimeout(resolve, 1000))
      await this.initializeChat()
    },

    cleanup() {
      chatService.disconnect()
      this.removeEventListeners()

       if (this.hiddenChatsScrollDebounce) {
        clearTimeout(this.hiddenChatsScrollDebounce)
      }
      if (this.chatChatsScrollDebounce) {
        clearTimeout(this.chatChatsScrollDebounce)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../views/css/styles.scss';

/* Base */
.chat-app {
  display: flex;
  flex-direction: column;
  background: linear-gradient(145deg, rgba(18, 18, 18, 1), rgba(10, 10, 10, 1));
  position: relative;
  border: 0px solid transparent;
  border-radius: 10px!important;
}

/* Loading & Error Screens */
.loading-screen, 
.error-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  min-height: calc(100dvh - 168px);
  padding: 40px 20px;
  border: 0px solid transparent;
  border-radius: 10px!important;
}

.warning-icon {
  color: #ffc107!important;
  animation: pulse2 2s ease-in-out infinite;
}

.error-icon {
  color: #dc3545!important;
  animation: pulse2 2s ease-in-out infinite;
}

@keyframes pulse2 {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

.loading-title,
.error-title {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: white;
  text-align: center;
  max-width: 400px;
}

.loading-text,
.error-text {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin: 0;
  max-width: 400px;
}

.settings-btn,
.reconnect-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  margin-top: 10px;
}

.settings-btn:hover,
.reconnect-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* Load More Container */
.load-more-container {
  padding: 16px 20px;
  display: flex;
  justify-content: center;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.2);
}

.btn-load-more {
  width: 100%;
  max-width: 280px;
  padding: 12px 20px;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.15), rgba(0, 86, 179, 0.15));
  border: 1px solid rgba(0, 123, 255, 0.4);
  border-radius: 10px;
  color: white;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
}

.btn-load-more:hover {
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.25), rgba(0, 86, 179, 0.25));
  border-color: rgba(0, 123, 255, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.3);
}

.btn-load-more:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
}

/* Responsive dla Load More */
@media (max-width: 768px) {
  .load-more-container {
    padding: 12px 16px;
  }

  .btn-load-more {
    max-width: 100%;
    padding: 10px 16px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .load-more-container {
    padding: 10px 12px;
  }

  .btn-load-more {
    padding: 8px 12px;
    font-size: 12px;
  }
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Desktop Interface */
.chat-interface {
  display: flex;

  border: 0px solid transparent;
  border-radius: 10px!important;
}

/* Sidebar */
.chat-sidebar {
  width: 350px;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.3);
  border-radius: 10px 0 0 10px!important;
}

/* Connection Status */
.connection-status {
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-family: "Darker Grotesque", sans-serif;
  font-weight: 600;
  color: #dc3545;
  transition: all 0.3s ease;
}

.connection-status.connected {
  color: #28a745;
  background: rgba(40, 167, 69, 0.1);
  border-left: 3px solid #28a745;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dc3545;
  animation: pulse 2s ease-in-out infinite;
}

.connection-status.connected .status-dot {
  background: #28a745;
}

.status-text {
  font-size: 13px;
}

/* Search Section */
.search-section {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 16px 10px 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  color: white;
  outline: none;
  transition: all 0.3s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.search-input:focus {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
}

.btn-clear {
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-clear:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

/* Notifications Section */
.notifications-section {
  padding: 12px 20px;
  background: rgba(255, 193, 7, 0.1);
  border-bottom: 1px solid rgba(255, 193, 7, 0.3);
  border-left: 3px solid #ffc107;
}

.notification-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: white;
  transition: all 0.2s ease;
}

.notification-badge:hover {
  padding-left: 4px;
}

.notification-text {
  flex: 1;
}

.notification-arrow {
  color: rgba(255, 255, 255, 0.6) !important;
  transition: transform 0.2s ease;
}

.notification-badge:hover .notification-arrow {
  transform: translateX(4px);
}

/* Chat Tabs */
.chat-tabs {
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.3);
  padding: 0;
}

.chat-tab {
  flex: 1;
  padding: 14px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  font-family: "Darker Grotesque", sans-serif;
  color: rgba(255, 255, 255, 0.6);
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  bottom: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.chat-tab:hover {
  color: white;
  background: rgba(255, 255, 255, 0.05);
}

.chat-tab.active {
  color: white;
  border-bottom-color: #007bff;
  background: rgba(0, 123, 255, 0.1);
}

.tab-count {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
}

.chat-tab.active .tab-count {
  background: #007bff;
}

/* Chat Actions */
.chat-actions {
  display: flex;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
}

.btn-action {
  flex: 1;
  padding: 10px 16px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.btn-action.primary {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.btn-action.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
}

.btn-action.secondary {
  background: rgba(255, 255, 255, 0.05);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  flex: 0 0 auto;
  padding: 10px 12px;
}

.btn-action.secondary:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

/* Chat List */
.chat-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 100%;
  min-height: 0;
  scrollbar-width: none; /* Firefox - ukrywa scrollbar */
  -ms-overflow-style: none; /* IE/Edge - ukrywa scrollbar */
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior: contain;
  outline: none;
}

.chat-list::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.chat-list::-webkit-scrollbar-track {
  display: none;
  background: transparent;
}

.chat-list::-webkit-scrollbar-thumb {
  display: none;
  background: transparent;
}

.chat-list::-webkit-scrollbar-thumb:hover {
  display: none;
  background: transparent;
}

/* Chat Item */
.chat-item {
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  gap: 14px;
  align-items: center;
  position: relative;
}

.chat-item:hover {
  background: rgba(0, 123, 255, 0.1);
}

.chat-item.active {
  background: rgba(0, 123, 255, 0.2);
  border-left: 3px solid #007bff;
}

.chat-item.unread {
  background: rgba(255, 193, 7, 0.05);
}

.chat-item.hidden-chat {
  opacity: 0.7;
}

.chat-item.hidden-chat:hover {
  opacity: 1;
}

/* Chat Avatar */
.chat-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 700;
  font-family: "Darker Grotesque", sans-serif;
  flex-shrink: 0;
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
  transition: transform 0.2s ease;
}

.chat-item:hover .chat-avatar {
  transform: scale(1.05);
}

.chat-avatar.hidden {
  background: linear-gradient(135deg, #6c757d, #495057);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.4);
}

.online-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #dc3545;
  border: 2px solid rgba(0, 0, 0, 0.9);
  transition: background 0.3s ease;
}

.online-indicator.online {
  background: #28a745;
  animation: pulse 2s ease-in-out infinite;
}

/* Chat Info */
.chat-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chat-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.chat-name {
  font-weight: 600;
  font-size: 15px;
  font-family: "Darker Grotesque", sans-serif;
  color: white;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.chat-item.unread .chat-name {
  font-weight: 800;
  color: #ffc107;
}

.group-icon,
.hidden-icon {
  color: rgba(255, 255, 255, 0.6) !important;
  flex-shrink: 0;
}

.chat-time {
  font-size: 12px;
  font-family: "Darker Grotesque", sans-serif;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  flex-shrink: 0;
}

.chat-preview {
  display: flex;
  align-items: center;
  gap: 8px;
}

.chat-preview-text {
  font-size: 13px;
  font-family: "Darker Grotesque", sans-serif;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.unread-badge {
  color: #ffc107;
  font-size: 16px;
  flex-shrink: 0;
  animation: pulse 2s ease-in-out infinite;
}

/* Chat Item Actions */
.chat-item-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.chat-item:hover .chat-item-actions {
  opacity: 1;
}

.btn-icon-small {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-icon-small:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
  transform: scale(1.1);
}

.btn-icon-small.danger {
  border-color: rgba(220, 53, 69, 0.5);
}

.btn-icon-small.danger:hover {
  background: rgba(220, 53, 69, 0.2);
  border-color: #dc3545;
}

.btn-icon-small.success {
  border-color: rgba(40, 167, 69, 0.5);
}

.btn-icon-small.success:hover {
  background: rgba(40, 167, 69, 0.2);
  border-color: #28a745;
}

/* Loading More */
.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  font-weight: 600;
}

.spinner-small {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  gap: 12px;
}

.empty-state h3 {
  margin: 16px 0 8px 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: white;
}

.empty-state p {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
}

.btn-create-first {
  margin-top: 16px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.btn-create-first:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
}

/* End of List */
.end-of-list {
  padding: 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-family: "Darker Grotesque", sans-serif;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.end-of-list span {
  font-style: italic;
}

/* Chat Main */
.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(145deg, rgba(18, 18, 18, 1), rgba(10, 10, 10, 1));
  border-radius: 0 10px 10px 0!important;
}

.no-chat-selected {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100dvh - 168px);
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  gap: 16px;
  padding: 40px 20px;
}

.no-chat-selected h3 {
  margin: 16px 0 8px 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: white;
}

.no-chat-selected p {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
}

/* Mobile Interface */
.chat-interface-mobile {
  position: relative;
  display: flex;
  overflow: hidden;
  border: 0px solid transparent;
  border-radius: 10px!important;
}

.chat-sidebar-mobile {
  position: fixed;
  left: -320px;
  top: 0px;
  width: 320px;
  height: calc(100dvh);
  padding-bottom: 120px;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border-right: 2px solid #007bff;
  display: flex;
  z-index: 1000;
  transition: left 0.3s ease-in-out;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);

}

.chat-sidebar-mobile.sidebar-open {
  left: 0;
}

/* Sidebar Tab */
.sidebar-tab {
  position: absolute;
  right: -35px;
  top: 50%;
  transform: translateY(-50%);
  width: 35px;
  height: 120px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 0 10px 10px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 4px 0 12px rgba(0, 123, 255, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;
  overflow: visible;
}

.chat-sidebar-mobile.sidebar-open .sidebar-tab {
  width: 30px;
  right: -30px;
  opacity: 0.8;
  background: linear-gradient(135deg, #0056b3, #003d82);
}

.sidebar-tab:hover {
  background: linear-gradient(135deg, #0056b3, #003d82);
  right: -30px;
  opacity: 1;
  box-shadow: 6px 0 16px rgba(0, 123, 255, 0.5);
}

.chat-sidebar-mobile.sidebar-open .sidebar-tab:hover {
  opacity: 1;
  width: 30px;
}

.tab-text {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  color: white;
  font-weight: 700;
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  letter-spacing: 1px;
  margin-bottom: 2px;
  transition: font-size 0.3s ease;
}

.chat-sidebar-mobile.sidebar-open .tab-text {
  font-size: 11px;
  letter-spacing: 0.5px;
}

.tab-icon {
  color: white !important;
  font-size: 24px !important;
  transition: transform 0.3s ease, font-size 0.3s ease;
}

.chat-sidebar-mobile.sidebar-open .tab-icon {
  font-size: 18px !important;
}

/* Sidebar Content */
.sidebar-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  max-height: 100%;
}

/* Chat Main Mobile */
.chat-main-mobile {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(145deg, rgba(18, 18, 18, 1), rgba(10, 10, 10, 1));
  transition: margin-left 0.3s ease-in-out;
  width: 100%;
  border-radius: 10px !important;
}

.chat-main-mobile.sidebar-open {
  margin-left: 0;
}

/* Sidebar Overlay */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 999;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Slide Down Animation */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 768px) {
  .chat-sidebar {
    width: 100%;
    max-width: 300px;
  }

  .search-section {
    padding: 12px 16px;
  }

  .chat-actions {
    padding: 12px 16px;
  }

  .chat-item {
    padding: 12px 16px;
  }

  .chat-avatar {
    width: 44px;
    height: 44px;
    font-size: 15px;
  }
}

/* Vuetify Overrides */
:deep(.v-btn:hover) > .v-btn__overlay {
  opacity: 0.1;
}

:deep(.v-icon) {
  color: inherit !important;
}

.chat-sidebar-mobile.sidebar-open .sidebar-tab {
  width: 30px;
  right: -30px;
  opacity: 0.8;
  background: linear-gradient(135deg, #0056b3, #003d82);
}

/* Tab Unread Badge */
.tab-unread-badge {
  position: absolute;
  top: -8px;
  right: 50%;
  transform: translateX(50%);
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  font-size: 10px;
  font-weight: 700;
  font-family: "Darker Grotesque", sans-serif;
  padding: 3px 6px;
  border-radius: 10px;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.5);
  animation: pulse 2s ease-in-out infinite;
  border: 2px solid rgba(0, 86, 179, 1);
  z-index: 1;
}

.chat-sidebar-mobile.sidebar-open .tab-unread-badge {
  top: 6px;
  font-size: 9px;
  padding: 2px 5px;
  min-width: 18px;
  height: 18px;
  border-width: 1.5px;
}

.sidebar-tab:hover .tab-unread-badge {
  background: linear-gradient(135deg, #ff4757, #ff3838);
  box-shadow: 0 3px 10px rgba(255, 71, 87, 0.6);
  transform: translateX(50%) scale(1.05);
}

@keyframes pulse {
  0%, 100% {
    transform: translateX(50%) scale(1);
    opacity: 1;
  }
  50% {
    transform: translateX(50%) scale(1.1);
    opacity: 0.9;
  }
}
</style>